import React, { useMemo, useState } from "react";
import { Dialog } from "components/dialog";
import { SubscriptionPlan } from "types";
import { subscriptionPlansApi } from "api/models";
import { handleAxiosAndCaughtError } from "utils";
import toast from "react-hot-toast";
import { CreditCard } from "lucide-react";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import { DropdownOption } from "components/pure-dropdown";
import { useTranslation } from "react-i18next";

interface ViewSubscriptionPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subscriptionPlan: SubscriptionPlan | null;
  frequencyOptions: DropdownOption[];
}

const ViewSubscriptionPlanDialog: React.FC<ViewSubscriptionPlanDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  subscriptionPlan,
  frequencyOptions,
}) => {
  const { t } = useTranslation();
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  // Memoize subscription plan status calculation
  const subscriptionPlanStatus = useMemo(() => {
    if (!subscriptionPlan) return null;
    
    if (subscriptionPlan.is_active) {
      return { text: t("subscriptions.status.active"), class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    } else {
      return { text: t("subscriptions.status.inactive"), class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    }
  }, [subscriptionPlan, t]);

  const handleToggleStatus = async () => {
    if (!subscriptionPlan) return;
    
    setIsTogglingStatus(true);
    try {
      if (subscriptionPlan.is_active) {
        await subscriptionPlansApi.deactivate(subscriptionPlan.id);
        toast.success(t("subscriptionPlans.dialog.view.deactivatedSuccessfully"));
      } else {
        await subscriptionPlansApi.activate(subscriptionPlan.id);
        toast.success(t("subscriptionPlans.dialog.view.activatedSuccessfully"));
      }
      await onSuccess();
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  if (!subscriptionPlan) return null;

  const configurationsText = JSON.stringify(subscriptionPlan.configurations, null, 2);

  return (
    <Dialog isOpen={isOpen} setIsOpen={onClose} outsideClickDisabled={true} backdropHandlerDisabled={true}>
      <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
        <div className="border-b border-gray-200 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("subscriptionPlans.dialog.view.title")}
            </h2>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {t("subscriptionPlans.dialog.view.description")}
          </p>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("common.name")}</div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white break-words">
                  {subscriptionPlan.name}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("common.status")}</div>
                <div className="mt-1">
                  {subscriptionPlanStatus && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscriptionPlanStatus.class}`}>
                      {subscriptionPlanStatus.text}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("subscriptionPlans.dialog.view.price")}</div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {subscriptionPlan.price?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || t("apiKeys.notAvailable")}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("subscriptionPlans.billingFrequency")}</div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {frequencyOptions.find(o => o.value === subscriptionPlan.frequency)?.label || subscriptionPlan.frequency}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("subscriptionPlans.dialog.view.enterprisePlan")}</div>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    id="view_is_enterprise"
                    type="checkbox"
                    checked={!!subscriptionPlan.is_enterprise}
                    readOnly
                    disabled
                    className="h-5 w-5 rounded-md border-gray-300 accent-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="view_is_enterprise" className="text-sm text-gray-900 dark:text-white">
                    {subscriptionPlan.is_enterprise ? t("subscriptionPlans.dialog.view.enabled") : t("subscriptionPlans.dialog.view.disabled")}
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("subscriptions.dialog.createdDate")}</div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {dayjs(subscriptionPlan.created_at).format(DATE_TIME_FORMAT)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("subscriptions.dialog.updatedDate")}</div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {dayjs(subscriptionPlan.updated_at).format(DATE_TIME_FORMAT)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptionPlans.dialog.create.configurationsLabel")}
            </label>
            <textarea
              value={configurationsText}
              readOnly
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              rows={8}
            />
          </div>
        </div>

        <div className="flex gap-3 pt-3 sm:pt-4">
          {subscriptionPlan.is_active ? (
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={isTogglingStatus}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-red-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-red-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-500 disabled:hover:shadow-md"
            >
              {isTogglingStatus ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <span>{t("subscriptionPlans.dialog.view.deactivate")}</span>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={isTogglingStatus}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-green-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-green-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500 disabled:hover:shadow-md"
            >
              {isTogglingStatus ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <span>{t("subscriptionPlans.dialog.view.activate")}</span>
              )}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ViewSubscriptionPlanDialog;

